import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Calendar, ArrowRight } from "lucide-react";

const SPECIALTIES = [
  "Geriatra", "Cardiologista", "Neurologista", "Clínico Geral", "Psiquiatra",
  "Psicólogo", "Fisioterapeuta", "Nutricionista", "Enfermeiro",
  "Fonoaudiólogo", "Terapeuta Ocupacional",
];

export default function FPRequestForm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [pros, setPros] = useState([]);
  const [form, setForm] = useState({
    beneficiary_id: params.get("beneficiary_id") || "",
    specialty: "",
    professional_id: params.get("professional_id") || "",
    modality: "presencial",
    desired_date: "",
    desired_time: "",
    priority: "normal",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/fp/beneficiaries").then((r) => setBeneficiaries(r.data));
    api.get("/fp/professionals").then((r) => setPros(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.beneficiary_id) return toast.error("Selecione o beneficiário");
    if (!form.specialty) return toast.error("Selecione a especialidade");
    if (!form.desired_date) return toast.error("Escolha a data");
    setSaving(true);
    try {
      const { data } = await api.post("/fp/requests/consulta", form);
      toast.success("Solicitação enviada! A Central RAF entrará em contato.");
      nav(`/filho-presente/solicitacoes/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar solicitação");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-request-form">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mb-2">Solicitar consulta</h1>
      <p className="text-slate-600 text-sm mb-6">Preencha os dados e a Central RAF vai coordenar o agendamento.</p>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div>
          <label className="block text-sm mb-1 font-medium">Para qual familiar? *</label>
          <select value={form.beneficiary_id} onChange={(e) => set("beneficiary_id", e.target.value)} required
            data-testid="fp-req-beneficiary"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
            <option value="">Selecione...</option>
            {beneficiaries.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1 font-medium">Especialidade *</label>
            <select value={form.specialty} onChange={(e) => set("specialty", e.target.value)} required
              data-testid="fp-req-specialty"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="">Selecione...</option>
              {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1 font-medium">Modalidade</label>
            <select value={form.modality} onChange={(e) => set("modality", e.target.value)}
              data-testid="fp-req-modality"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
              <option value="domiciliar">Domiciliar</option>
            </select>
          </div>
        </div>

        {form.specialty && pros.filter((p) => p.category === form.specialty || p.specialty === form.specialty).length > 0 && (
          <div>
            <label className="block text-sm mb-1 font-medium">Profissional (opcional)</label>
            <select value={form.professional_id} onChange={(e) => set("professional_id", e.target.value)}
              data-testid="fp-req-pro"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="">Sem preferência (Central RAF sugere)</option>
              {pros.filter((p) => p.category === form.specialty || p.specialty === form.specialty)
                .map((p) => <option key={p.id} value={p.id}>{p.display_name}{p.city ? ` · ${p.city}` : ""}</option>)}
            </select>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1 font-medium">Data desejada *</label>
            <input type="date" value={form.desired_date} onChange={(e) => set("desired_date", e.target.value)}
              min={new Date().toISOString().slice(0, 10)} required
              data-testid="fp-req-date"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-sm mb-1 font-medium">Horário</label>
            <input type="time" value={form.desired_time} onChange={(e) => set("desired_time", e.target.value)}
              data-testid="fp-req-time"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1 font-medium">Prioridade</label>
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "normal", l: "Normal" },
              { v: "prioritario", l: "Prioritário" },
              { v: "urgente", l: "Urgente" },
            ].map((o) => (
              <button key={o.v} type="button" onClick={() => set("priority", o.v)}
                data-testid={`fp-req-priority-${o.v}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  form.priority === o.v ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-600 border-slate-300"
                }`}>{o.l}</button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            ⚠️ Urgente = prioridade operacional interna. Para emergência médica ligue SAMU 192.
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1 font-medium">Observações</label>
          <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
            data-testid="fp-req-notes"
            placeholder="Contexto do atendimento, sintomas, preferências..."
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>

        <button type="submit" disabled={saving} data-testid="fp-req-submit"
          className="w-full bg-[#0B2B62] text-white py-3 rounded-lg hover:bg-slate-800 disabled:opacity-50 font-medium inline-flex items-center justify-center gap-2">
          {saving ? "Enviando..." : <>Solicitar <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}
